import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PlugsIcon, StorefrontIcon, WarningIcon } from '@phosphor-icons/react';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusChip,
  fmtRelativeTime,
  type Tone,
} from '@smena/ui';
import { STEPS } from '@/features/setup/api';
import { disconnectAvito, fetchAvitoStatus, type AvitoConnectionView } from './api';

/**
 * «Авито» на странице интеграций: показать состояние подключения и дать его снять.
 *
 * Подключение живёт в мастере (`/setup`, шаг «Авито»), а вот отключения не было нигде:
 * ручка `DELETE /api/staff/avito` работала, `disconnectAvito()` лежал в api.ts мёртвым кодом.
 * УК, которая передумала или сменила ключи, не могла ничего сделать из панели.
 *
 * Подключать отсюда намеренно не даём - форма ключей и OAuth-путь остаются в мастере,
 * иначе получится два расходящихся места с одной и той же формой.
 */
export function AvitoCard() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const q = useQuery({ queryKey: ['avito-status'], queryFn: fetchAvitoStatus });

  const disconnectMut = useMutation({
    mutationFn: disconnectAvito,
    onSuccess: () => {
      setConfirm(false);
      toast.success('Авито отключён');
      void qc.invalidateQueries({ queryKey: ['avito-status'] });
      void qc.invalidateQueries({ queryKey: ['integrations-health'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось отключить Авито'),
  });

  const conn = q.data?.connection ?? null;
  const setupHref = `/setup?step=${STEPS.indexOf('avito') + 1}`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <StorefrontIcon weight="bold" className="size-4 text-accent-fg" /> Авито
        </CardTitle>
        {/* На ошибке чипа нет: «не подключено» рядом с «не удалось получить статус» - враньё. */}
        {q.isLoading || q.isError ? null : (
          <StatusChip tone={statusTone(conn)}>{statusLabel(conn)}</StatusChip>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-3 text-sm">
        {q.isLoading ? (
          <p className="text-muted">Загружаем…</p>
        ) : q.isError ? (
          <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-3 text-xs text-red">
            <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
            <span>
              Не удалось получить статус:{' '}
              {q.error instanceof ApiError ? q.error.message : 'ошибка сервера'}.
            </span>
          </div>
        ) : !conn ? (
          <>
            <p className="text-muted">
              Из Авито Smena забирает объявления в черновики объектов и историю переписок в базу
              знаний. Подключение живёт в мастере, шаг «Авито»: там форма для ключей из личного
              кабинета Авито и подтверждение доступа, если оно доступно на этом стенде.
            </p>
            <div>
              <Button asChild size="sm" variant="secondary">
                <Link to={setupHref}>
                  <PlugsIcon weight="bold" className="size-4" /> Открыть мастер подключения
                </Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1 rounded-md border border-line bg-panel2/40 p-3 text-xs text-muted">
              <div>
                {conn.authKind === 'authorization_code' ? (
                  <>Подключено подтверждением на Авито</>
                ) : (
                  <>
                    client_id: <code className="font-mono">{conn.clientId.slice(0, 8)}…</code>
                  </>
                )}
                {conn.avitoUserId ? <> · avito_user_id: {conn.avitoUserId}</> : null}
              </div>
              <div>
                {conn.lastSyncAt
                  ? `Последняя синхронизация: ${fmtRelativeTime(conn.lastSyncAt)}`
                  : 'Синхронизаций ещё не было'}
              </div>
            </div>

            {conn.lastError ? (
              <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-3 text-xs text-red">
                <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
                <span>{conn.lastError}</span>
              </div>
            ) : null}

            <div className="border-t border-line pt-3">
              {!confirm ? (
                <Button variant="ghost" size="sm" onClick={() => setConfirm(true)}>
                  Отключить
                </Button>
              ) : (
                <div className="flex flex-col gap-2">
                  {/* Последствие называем прямо: ключи стираются, и оба импорта встают. */}
                  <span className="text-xs text-amber">
                    Ключи Авито будут удалены. Объявления и брони с Авито перестанут подтягиваться,
                    уже импортированные объекты и переписки останутся. Чтобы вернуть - введите ключи
                    заново в мастере.
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={disconnectMut.isPending}
                      onClick={() => disconnectMut.mutate()}
                    >
                      {disconnectMut.isPending ? 'Отключаем…' : 'Всё равно отключить'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
                      Отмена
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Чип статуса. `tariffOk` важнее поля `status`: без тарифа на Messenger API ключи валидны,
 * но импорт переписок не работает - это отдельное состояние, а не «подключено».
 */
function statusTone(conn: AvitoConnectionView | null): Tone {
  if (!conn) return 'muted';
  if (conn.tariffOk) return 'green';
  if (conn.status === 'no_tariff' || conn.status === 'error') return 'red';
  return 'amber';
}

function statusLabel(conn: AvitoConnectionView | null): string {
  if (!conn) return 'не подключено';
  if (conn.tariffOk) return 'подключено';
  if (conn.status === 'no_tariff') return 'нет тарифа';
  if (conn.status === 'error') return 'ошибка';
  return 'подключено, тариф не проверен';
}
